import { addResponseStatusToSpan } from '@vs-shop/engine/utils/httpTracing';
import { Effect } from 'effect';

import type { BackendApiErrorUnion } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { GlossaryTermIndex } from '@vs-shop/schemas/branded/MiscSchema';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { logApiOperation } from '@vs-shop/engine/utils/logger/apiLogger';
import { matchStatus } from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/glossary-terms';
const HTTP_METHOD = 'get';

const ListGlossaryTermsError = createBackendApiError({
  domain: 'GlossaryTerm',
  endpoint: 'List',
  path: ENDPOINT,
  identifierField: 'filter',
} as const);

export type GlossaryTermListError = BackendApiErrorUnion<typeof ListGlossaryTermsError>;

const filterKey = (
  alphabet: string | null,
  name: string | null,
  onlyFavorites: boolean,
  page: number,
  itemsPerPage: number
): string => JSON.stringify({ alphabet, name, onlyFavorites, page, itemsPerPage });

const spanAttributes = (
  alphabet: string | null,
  name: string | null,
  onlyFavorites: boolean,
  page: number,
  itemsPerPage: number
) => ({
  'glossary.page': page,
  'glossary.itemsPerPage': itemsPerPage,
  ...(alphabet !== null ? { 'glossary.alphabet': alphabet } : {}),
  ...(name !== null ? { 'glossary.name': name } : {}),
  'glossary.onlyFavorites': onlyFavorites,
});

const listGlossaryTermsFetch = (
  alphabet: string | null,
  name: string | null,
  onlyFavorites: boolean,
  page: number,
  itemsPerPage: number
) =>
  Effect.gen(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({
        query: {
          page,
          itemsPerPage,
          ...(alphabet !== null ? { alphabet } : {}),
          ...(name !== null ? { name } : {}),
          ...(onlyFavorites ? { onlyFavorites: true } : {}),
        },
      });
  }).pipe(
    Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
      kind: 'client',
      attributes: {
        'http.request.method': HTTP_METHOD,
        ...spanAttributes(alphabet, name, onlyFavorites, page, itemsPerPage),
      },
    })
  );

export const requestGlossaryTerms = (
  alphabet: string | null,
  name: string | null,
  onlyFavorites: boolean,
  page: number,
  itemsPerPage: number
) =>
  listGlossaryTermsFetch(alphabet, name, onlyFavorites, page, itemsPerPage).pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.withSpan('ValidateResponse'),
    Effect.flatMap(
      matchStatus({
        '2xx': (response) =>
          schemaBodyJsonClean(HydraCollectionResponse(GlossaryTermIndex))(response),
        404: (response) =>
          Effect.fail(
            new ListGlossaryTermsError.NotFoundError({
              filter: filterKey(alphabet, name, onlyFavorites, page, itemsPerPage),
              response,
            })
          ),
        orElse: (response) =>
          Effect.fail(
            new ListGlossaryTermsError.ApiRequestError({
              filter: filterKey(alphabet, name, onlyFavorites, page, itemsPerPage),
              response,
            })
          ),
      })
    ),
    Effect.tap((result) =>
      logApiOperation(
        ApiModule.GlossaryTermList,
        'Debug',
        `Resolved ${String(result['hydra:member'].length)} glossary terms${alphabet !== null ? ` [alphabet=${alphabet}]` : ''}${name !== null ? ` [name=${name}]` : ''}${onlyFavorites ? ' [favorites]' : ''} [page=${String(page)} itemsPerPage=${String(itemsPerPage)}]`
      )
    ),
    Effect.withSpan('api.glossaryTerm.list', {
      kind: 'internal',
      attributes: spanAttributes(alphabet, name, onlyFavorites, page, itemsPerPage),
    })
  );
